import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useSchool } from '../context/SchoolContext';
import { Sparkles, CalendarIcon, Loader2, Copy, RefreshCw, History, Clock, FileDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

export const AISummaryPage = () => {
  const { classId } = useParams();
  const { selectedSchool } = useSchool();
  const [classInfo, setClassInfo] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [generating, setGenerating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [summaryHistory, setSummaryHistory] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);

  const periodsPerDay = selectedSchool?.periods_per_day || 6;
  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  useEffect(() => {
    const fetchClassInfo = async () => {
      try {
        const r = await fetch(`${API}/classes`, { credentials: 'include' });
        if (r.ok) { const classes = await r.json(); setClassInfo(classes.find(c => c.class_id === classId)); }
      } catch (error) { console.error('Failed to fetch class info:', error); }
    };
    fetchClassInfo();
  }, [classId]);

  const fetchHistory = async () => {
    try {
      const r = await fetch(`${API}/ai-summary-history/${classId}`, { credentials: 'include' });
      if (r.ok) setSummaryHistory(await r.json());
    } catch (error) { console.error('Failed to fetch history:', error); }
  };

  const handleGenerateSummary = async () => {
    setGenerating(true); setSummary('');
    try {
      const body = {
        class_id: classId,
        date: dateStr,
        additional_notes: additionalNotes || undefined,
      };
      if (selectedPeriod && selectedPeriod !== 'all') body.period = parseInt(selectedPeriod);

      const r = await fetch(`${API}/ai-summary`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body)
      });
      if (r.ok) { const data = await r.json(); setSummary(data.summary); toast.success('Summary generated!'); }
      else { const error = await r.json(); toast.error(error.detail || 'Failed to generate summary'); }
    } catch (error) { toast.error('Failed to generate summary'); }
    finally { setGenerating(false); }
  };

  const handleCopy = (text) => { navigator.clipboard.writeText(text || summary); toast.success('Copied to clipboard!'); };

  const handleOpenHistory = () => { fetchHistory(); setHistoryOpen(true); };

  const handleExportPDF = async () => {
    if (!summary) return;
    const html2pdf = (await import('html2pdf.js')).default;
    const periodText = selectedPeriod && selectedPeriod !== 'all' ? ` (Period ${selectedPeriod})` : '';
    const element = document.createElement('div');
    element.innerHTML = `
      <div style="font-family: Helvetica, Arial, sans-serif; padding: 40px;">
        <div style="border-bottom: 3px solid #4338ca; padding-bottom: 16px; margin-bottom: 24px;">
          <h1 style="font-size: 22px; color: #1e293b; margin: 0;">Lesson Summary</h1>
          <p style="font-size: 14px; color: #64748b; margin: 8px 0 0;">${classInfo?.name || 'Class'} &mdash; ${dateStr}${periodText}</p>
        </div>
        <div style="font-size: 13px; color: #334155; line-height: 1.8; white-space: pre-wrap;">${summary}</div>
        <div style="border-top: 1px solid #e2e8f0; margin-top: 32px; padding-top: 12px;">
          <p style="font-size: 10px; color: #94a3b8;">Generated by Reliefify</p>
        </div>
      </div>
    `;
    html2pdf().set({
      margin: 10, filename: `Summary_${classInfo?.name || 'class'}_${dateStr}.pdf`,
      html2canvas: { scale: 2 }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    }).from(element).save();
    toast.success('PDF downloaded!');
  };

  const breadcrumb = [
    { label: 'Classes', path: '/app/dashboard' },
    { label: classInfo?.name || 'Class', path: `/class/${classId}` },
    { label: 'AI Summary' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="ai-summary-page">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-50 mb-4">
              <Sparkles className="w-7 h-7 text-teal-600" />
            </div>
            <h1 className="text-2xl font-heading font-semibold text-slate-900">AI Lesson Summary</h1>
            <p className="text-slate-500 mt-2 text-sm">Generate a detailed report for handover</p>
          </div>
          <Button variant="outline" onClick={handleOpenHistory} className="border-slate-200 text-slate-600 rounded-xl absolute right-8 top-24 lg:relative lg:right-auto lg:top-auto" data-testid="history-btn">
            <History className="w-4 h-4 mr-2" />History
          </Button>
        </div>

        <Card className="bg-white border border-slate-200 rounded-xl shadow-soft">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Date picker */}
              <div>
                <label className="text-sm font-medium text-slate-600">Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start mt-1.5 border-slate-200 rounded-xl text-sm" data-testid="summary-date-picker">
                      <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />{format(selectedDate, 'EEE, MMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Period selector */}
              <div>
                <label className="text-sm font-medium text-slate-600">Period</label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="mt-1.5 border-slate-200 rounded-xl" data-testid="period-selector">
                    <SelectValue placeholder="All periods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All periods</SelectItem>
                    {Array.from({ length: periodsPerDay }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>Period {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-600">Additional Notes (optional)</label>
              <Textarea
                placeholder="Topics covered, special events, anything noteworthy..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                className="mt-1.5 border-slate-200 rounded-xl min-h-[80px] text-sm"
                data-testid="additional-notes-input"
              />
            </div>
            <Button onClick={handleGenerateSummary} disabled={generating} className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11 rounded-xl" data-testid="generate-summary-btn">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating detailed summary...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate Summary</>}
            </Button>
          </CardContent>
        </Card>

        {summary && (
          <Card className="bg-white border border-slate-200 animate-fade-in rounded-xl shadow-soft">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading font-semibold flex items-center gap-2 text-slate-800">
                  <Sparkles className="w-4 h-4 text-teal-600" />Generated Summary
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-slate-200 rounded-lg text-xs" onClick={() => handleCopy()} data-testid="copy-summary-btn">
                    <Copy className="w-3.5 h-3.5 mr-1" />Copy
                  </Button>
                  <Button variant="outline" size="sm" className="border-slate-200 rounded-lg text-xs" onClick={handleGenerateSummary} disabled={generating}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />Regenerate
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportPDF} className="border-slate-200 rounded-lg text-xs" data-testid="export-pdf-btn">
                    <FileDown className="w-3.5 h-3.5 mr-1" />PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="bg-slate-50 rounded-xl p-5 text-sm leading-relaxed whitespace-pre-wrap text-slate-700" data-testid="summary-content">{summary}</div>
            </CardContent>
          </Card>
        )}

        {/* History Dialog */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900">Summary History</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-3 pt-2">
              {summaryHistory.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No summaries generated yet</p>
              ) : selectedHistory ? (
                <div className="space-y-3">
                  <Button variant="ghost" onClick={() => setSelectedHistory(null)} className="text-teal-600 text-sm">&larr; Back to list</Button>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-sm font-medium text-slate-700">{selectedHistory.date}</span>
                        {selectedHistory.period && <span className="ml-2 text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-md">Period {selectedHistory.period}</span>}
                      </div>
                      <Button variant="outline" size="sm" className="border-slate-200 rounded-lg text-xs" onClick={() => handleCopy(selectedHistory.summary)}>
                        <Copy className="w-3.5 h-3.5 mr-1" />Copy
                      </Button>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="history-summary-content">{selectedHistory.summary}</div>
                  </div>
                </div>
              ) : (
                summaryHistory.map((item) => (
                  <button
                    key={item.summary_id}
                    className="w-full text-left p-4 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                    onClick={() => setSelectedHistory(item)}
                    data-testid={`history-item-${item.summary_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{item.date}</span>
                        {item.period && <span className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-md">P{item.period}</span>}
                      </div>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <Clock className="w-3 h-3" />
                        {item.created_at && format(parseISO(item.created_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{item.summary?.slice(0, 150)}...</p>
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
